import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { CatalogSong } from '../../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../../common/types';
import { LyricTimingSection } from '../../lyricTiming/LyricTimingSection';
import { catalogSongMessages } from '../../catalogSongMessages';
import { SongLyricsView } from '../catalogSongPath';
import { SONG_LYRICS_MAX_LENGTH } from '../draft/songPageDraft';
import { SongPageDraftState } from '../draft/useSongPageDraft';
import { songPageMessages } from '../songPageMessages';
import { SongTimesMakeCard } from './SongTimesMakeCard';
import { Panel, PanelBody } from '../../../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from '../SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The id of the lyrics text box, so another part of the page can put the focus in it. */
export const SONG_LYRICS_INPUT_ID = 'song-page-lyrics';

interface SongLyricsViewSwitchProps {
  view: SongLyricsView;
  onChange: (view: SongLyricsView) => void;
}

/** "Times" / "Text" (design d1, d2). It sits in the head of the Lyrics section. */
export const SongLyricsViewSwitch: React.FC<SongLyricsViewSwitchProps> = ({ view, onChange }) => {
  const intl = useIntl();
  return (
    <div className={styles.segment} role="tablist" aria-label={intl.formatMessage(songPageMessages.lyricsView)}>
      {(['times', 'text'] as const).map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={view === value}
          className={clsx(styles.segmentItem, view === value && styles.segmentOn)}
          onClick={() => onChange(value)}
        >
          {intl.formatMessage(value === 'times' ? songPageMessages.viewTimes : songPageMessages.viewText)}
        </button>
      ))}
    </div>
  );
};

interface SongLyricsSectionProps {
  accountId: number;
  itemUuid: string;
  title: string;
  song: CatalogSong;
  form: SongPageDraftState;
  view: SongLyricsView;
  onViewChange: (view: SongLyricsView) => void;
  onOpenFiles: () => void;
  readOnly: boolean;
}

/**
 * Lyrics (design d1 to d8). Times is the lyric timing editor of design s8: a time saves at once
 * (owner decision 1). Text edits the words; a change waits in the save bar, because a save starts
 * a sync. The master player is the bar at the top of the page.
 */
const SongLyricsSectionComponent: React.FC<SongLyricsSectionProps> = ({
  accountId,
  itemUuid,
  title,
  song,
  form,
  view,
  onViewChange,
  onOpenFiles,
  readOnly,
}) => {
  const intl = useIntl();
  const { draft, setField, errors } = form;
  const hasServerLyrics = song.lyrics !== null && song.lyrics.trim() !== '';
  const showText = () => {
    onViewChange('text');
    window.requestAnimationFrame(() => document.getElementById(SONG_LYRICS_INPUT_ID)?.focus());
  };
  const change = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => setField('lyrics', event.target.value),
    [setField]
  );

  return (
    <div className={styles.cols}>
      <div className={styles.stack}>
        {view === 'times' ? (
          <LyricTimingSection
            accountId={accountId}
            itemUuid={itemUuid}
            song={song}
            isPage
            readOnly={readOnly}
            onAddLyrics={readOnly ? undefined : showText}
            noMasterAction={{ label: intl.formatMessage(songPageMessages.sectionFiles), onClick: onOpenFiles }}
          />
        ) : (
          <Panel>
            <PanelBody className={styles.cardBody}>
              {!hasServerLyrics && draft.lyrics === '' && (
                <div className={styles.empty}>
                  <b>{intl.formatMessage(songPageMessages.lyricsEmptyTitle, { title })}</b>
                  <p>{intl.formatMessage(songPageMessages.lyricsEmptyBody)}</p>
                </div>
              )}
              <div className={styles.field}>
                <div className="d-flex justify-content-between align-items-center">
                  <label className={styles.label} htmlFor={SONG_LYRICS_INPUT_ID}>
                    {intl.formatMessage(catalogSongMessages.lyrics)}
                  </label>
                  <span className={styles.counter}>
                    {intl.formatMessage(songPageMessages.lyricsCount, {
                      length: intl.formatNumber(draft.lyrics.length),
                      max: intl.formatNumber(SONG_LYRICS_MAX_LENGTH),
                    })}
                  </span>
                </div>
                <textarea
                  id={SONG_LYRICS_INPUT_ID}
                  className={styles.textarea}
                  value={draft.lyrics}
                  readOnly={readOnly}
                  maxLength={SONG_LYRICS_MAX_LENGTH}
                  placeholder={intl.formatMessage(catalogSongMessages.lyricsPlaceholder)}
                  onChange={change}
                  data-testid="song-page-lyrics-text"
                />
                {errors.lyrics === 'tooLong' ? (
                  <span className={styles.errorText} role="alert">
                    {intl.formatMessage(songPageMessages.tooLong, { max: SONG_LYRICS_MAX_LENGTH })}
                  </span>
                ) : (
                  <span className={styles.help}>{intl.formatMessage(songPageMessages.lyricsHelp)}</span>
                )}
              </div>
            </PanelBody>
          </Panel>
        )}
      </div>
      <SongTimesMakeCard />
    </div>
  );
};

export const SongLyricsSection = TypedMemo(SongLyricsSectionComponent);
